import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, ApiError } from '@/lib/api';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import {
  Badge,
  Button,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import {
  ALLOCATION_IN_XERO_LABEL,
  ALLOCATION_NOT_IN_XERO_LABEL,
  CREDIT_AVAILABLE_LABEL,
  CREDIT_REMAINING_LABEL,
  SEND_ALLOCATION_TO_XERO_LABEL,
  UNAPPLY_REMOVES_FROM_XERO,
  XERO_ALLOCATION_REMOVE_FAILED_CODE,
  allocationInXero,
  allocationPushOutcome,
  spendableOf,
  type XeroOutcomeLine,
} from '@/lib/credit-notes';
import { cx } from '@/lib/cx';
import { sumDecimalStrings } from '@/lib/decimal';
import { documentDisplayTitle, formatDateTime, formatDecimalString } from '@/lib/format';
import type { CreditNote, CreditNoteAllocation } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * WHAT THIS CREDIT NOTE HAS BEEN APPLIED TO — and the two controls that move it.
 *
 * Every row here is money that has already left the note and landed on an
 * invoice, so three of this file's rules are about not asserting things the
 * data does not support, and the rest are about the two writes.
 *
 * ## ⛔ THE NO-HELPER-TEXT PROHIBITION
 *
 * This app forbids helper text outright. **Every sentence in this file is one
 * of the four documented exceptions**, and each constant below says which one
 * it claims, exactly as `@/lib/credit-notes` does — so a future sweep can tell
 * a warning about money from a tip.
 *
 * ```
 * EXCEPTION 1  a REFUSAL — the server said no, or will, and why
 * EXCEPTION 2  WHY A CONTROL IS DISABLED
 * EXCEPTION 3  a DESTRUCTIVE CONSEQUENCE — ConfirmDialog body text
 * EXCEPTION 4  a FACT ABOUT THE DATA the reader cannot otherwise see
 * ```
 *
 * Column headings, field labels and the `EmptyState` description are **labels**,
 * not helper text, and need no exception — the house rule *requires* an empty
 * state to say what to do next.
 *
 * ## 🔴 THE THREE THAT COST MONEY
 *
 * **1. `spendable` AND `remainingAmount` ARE BOTH REQUIRED TO OFFER THE FORM.**
 * `remainingAmount` is reported on a DRAFT too — a draft holds its whole value
 * there and can be spent on nothing — so a control offered wherever the figure
 * is above zero is offered on every draft. See {@link applyDenial}.
 *
 * **2. MONEY IS AN EXACT DECIMAL STRING.** Never `Number()`, never `parseFloat`,
 * never `?? 0`. Figures render through `formatDecimalString`; the amount box is
 * validated through `sumDecimalStrings`, which is the shared exact parser —
 * see {@link amountRefusal} for why a second decimal parser is not written here.
 *
 * **3. NOTHING IN THIS PANEL SUMS ANYTHING.** `allocatedTotal` and
 * `remainingAmount` on the note are the server's sums of these rows, and
 * `CreditNoteAllocation`'s own type says not to re-sum them in the browser to
 * second-guess the header. There is no total row and no cross-currency figure
 * anywhere here: a credit balance is per company per currency and nothing
 * converts.
 */
export interface CreditNoteAllocationsPanelProps {
  creditNote: CreditNote;
  /**
   * Re-read the credit note **and the invoice that was allocated to**.
   *
   * 🔴 **TWO FIGURES GO STALE ON EVERY WRITE HERE AND NEITHER RESPONSE REPORTS
   * THEM.** A create moves `allocatedTotal` and `remainingAmount` on the note;
   * a delete moves those **and** changes what the invoice owes, and answers
   * `204` — no body, so nothing carries either figure back. **An absent report
   * is not evidence that nothing moved.**
   *
   * ⚠️ It is called AFTER this panel has refreshed its own list, so the two
   * reads cannot interleave into a screen where the rows and the header
   * disagree about what has been spent.
   */
  onChanged: () => void;
  /**
   * Bumped by the page when something OUTSIDE this panel moved the
   * allocations' Xero state — approving the note pushes every allocation not
   * yet in Xero, and nothing else would tell this panel's own list to re-read.
   * Any change re-lists; the value itself means nothing.
   */
  reloadSignal?: number;
}

/* -------------------------------------------------------------------------
 * Wording. One copy each.
 *
 * ⚠️ These live here rather than in `@/lib/credit-notes` only because this
 * session owns one file. **They are candidates to move** the moment a second
 * surface needs to word an allocation — the module is the one decision point
 * for this vocabulary, and two copies of a sentence about destroying money is
 * exactly what it exists to prevent.
 * --------------------------------------------------------------------- */

/** The panel's own name. **A label**, not helper text. */
const PANEL_TITLE = 'Applied to invoices';

/**
 * ⚠️ **"Applied", matching {@link CREDIT_AVAILABLE_LABEL}'s own vocabulary
 * note** — the word has to match the thing that is undone. Un-applying DELETES
 * the allocation row, so a reader who applied credit can be told precisely what
 * disappears. "Used" or "spent" implies a transaction with a counterpart, which
 * is the reading {@link UNAPPLY_TITLE}'s dialog exists to prevent.
 */
const UNAPPLY_LABEL = 'Un-apply';
const UNAPPLY_TITLE = 'Un-apply this credit?';
const APPLY_LABEL = 'Apply credit';

/**
 * The empty state. **A label plus the structural `EmptyState` rule**, which
 * requires an empty state to say what to do next.
 *
 * ⚠️ **It must not imply an allocation is on its way.** A credit note nobody
 * has spent is the ordinary state of a note that was raised this morning, not
 * a gap waiting to be filled.
 */
const NOTHING_APPLIED_TITLE = 'None of this credit has been applied yet';

/**
 * 🔴 **EXCEPTION 2 — why the form is withheld.** A bare `disabled` makes a
 * permission denial, a draft and a fully-spent note identical, which is the one
 * thing a reader cannot recover from on this panel.
 *
 * ⚠️ **It says *spendable*, never *approved*.** `isApproved` and `isVoid` are
 * not complements — an approved note that was later voided carries both — so a
 * sentence built on the flag describes withdrawn money as live.
 */
const NOT_SPENDABLE_REASON =
  'This credit note is not spendable, so none of it can be applied to an invoice. A credit note counts once it is approved and while it has not been voided.';

/**
 * **EXCEPTION 2.** ⚠️ It names the control that DOES exist rather than
 * asserting an absence: *"there is nothing left"* is a dead end, and a positive
 * claim fails visibly if the control is ever renamed.
 */
const NOTHING_LEFT_REASON = `All of this credit note has been applied. ${UNAPPLY_LABEL} one of the rows below to free credit up again.`;

/** **EXCEPTION 1** — refusals, raised before the server can be asked. */
const INVOICE_REQUIRED = 'Choose the invoice this credit is applied to.';
const AMOUNT_REQUIRED = 'Enter the amount of credit to apply.';
const AMOUNT_NOT_A_FIGURE = 'Enter the amount as a figure, for example 250.00.';
/**
 * ⚠️ **This one is raised here because the server's own guard is a `CHECK
 * (amount > 0)`**, which arrives as a database errno naming no field at all —
 * a refusal a form cannot route to a control and a reader cannot act on.
 */
const AMOUNT_NOT_POSITIVE = `An allocation has to be more than zero. To give credit back to this note, ${UNAPPLY_LABEL.toLowerCase()} one of the rows below instead.`;

/**
 * **EXCEPTION 4** — a fact about the data. The picker's list could not be read,
 * and an empty picker is indistinguishable from there being nothing to pick.
 */
const INVOICES_UNREADABLE =
  'The invoices on this company could not be loaded, so there is nothing to choose from here yet.';

/**
 * **EXCEPTION 4.** ⚠️ **Worded "not settled", never "outstanding" or "owed"** —
 * `InvoiceListParams.unsettled` is wider than "awaiting payment" and a draft
 * nobody has sent is unsettled too. Its own type says not to label a control
 * built on it without saying drafts are in it.
 */
const NO_INVOICES_TO_APPLY_TO = (currency: string) =>
  `This company has no unsettled invoices in ${currency} to apply credit to.`;

/**
 * **EXCEPTION 4** — the picker was cut short by the server's page ceiling, and
 * the option somebody came for may be past it. The same standing as any other
 * `Combobox` truncation footnote.
 */
const PICKER_TRUNCATED = (shown: number, total: number) =>
  `Showing the first ${shown} of ${total}. Type to search the rest.`;

/** **EXCEPTION 4** — the list on screen may not be what the server holds. */
const LIST_UNREADABLE = 'The allocations on this credit note could not be loaded.';

/* -------------------------------------------------------------------------
 * Money, without ever touching a float.
 * --------------------------------------------------------------------- */

/**
 * Whether an exact decimal string is above zero.
 *
 * ⚠️ **It parses through `sumDecimalStrings` rather than with a regex of its
 * own**, so this file owns no second decimal parser — `@/lib/decimal` is where
 * that lives, and a second one would agree on the day it was written and drift
 * at the first correction. What is added here is a **comparison**, which that
 * module does not export and which this session may not add to it.
 *
 * ⛔ **No `Number()` anywhere in the test.** A normalised non-negative decimal
 * is above zero **iff it contains a non-zero digit** — exact, at any magnitude,
 * and true of `"0.00"`, `"0"` and `"0.000"` alike.
 */
function isPositiveDecimal(value: string): boolean {
  const normalised = sumDecimalStrings([value.trim()]);
  if (normalised === null) return false;
  if (normalised.startsWith('-')) return false;
  return /[1-9]/.test(normalised);
}

/**
 * Why a typed amount cannot be sent, or `null`.
 *
 * ⚠️ **It refuses a zero and a negative and NOTHING ELSE.** The amount is
 * bounded at both ends by figures this app cannot see — the note's remaining
 * value and the invoice's own outstanding balance, both of which move under a
 * live screen — and **a stale client-side maximum refuses a legitimate
 * allocation with a sentence of ours that is simply wrong.** Those two are the
 * server's to state, and its refusals name the field.
 *
 * ⚠️ **A third decimal place is deliberately NOT refused here.** A
 * client-side rule with no server rule behind it is a convention dressed as a
 * contract; if the column will not hold it, the server says so in its own
 * words.
 */
function amountRefusal(typed: string): string | null {
  const trimmed = typed.trim();
  if (trimmed === '') return AMOUNT_REQUIRED;
  if (sumDecimalStrings([trimmed]) === null) return AMOUNT_NOT_A_FIGURE;
  if (!isPositiveDecimal(trimmed)) return AMOUNT_NOT_POSITIVE;
  return null;
}

/**
 * A figure with its code, always — even when the panel shows only one currency.
 *
 * ⚠️ **The code is what makes a sum visibly refuse**: a bare number is one
 * somebody adds to the number beside it, and there is no honest total of these
 * rows.
 *
 * ⚠️ **An allocation carries no currency of its own, and the note's is the
 * right one to render** — the server refuses an allocation whose invoice is in
 * another currency, so this is the code the row was actually struck in rather
 * than a guess made to fill a column.
 */
function money(amount: string, currency: string): string {
  return `${formatDecimalString(amount)} ${currency}`;
}

/* -------------------------------------------------------------------------
 * The invoice a row points at.
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **TWO ABSENCES, AND THEY ARE DIFFERENT FACTS.** A `null` invoice is an
 * allocation whose target this read could not resolve; a `null` subject is a
 * real invoice with no title. `invoice?.subject ?? '—'` collapses them into one
 * dash, which reports a resolvable invoice as unresolvable.
 */
function invoiceName(allocation: CreditNoteAllocation): string | null {
  const invoice = allocation.invoice;
  if (invoice === null) return null;
  return invoice.subject?.trim() || `Invoice #${invoice.id}`;
}

const emptyDraft = () => ({ invoiceId: '', amount: '' });

/**
 * ⛔ **THE 422-ROUTING LIST IS DERIVED FROM THE DRAFT, NEVER WRITTEN OUT.** A
 * hand-kept list drifts from the controls, and the failure is silent: the form
 * reports *"one field needs attention, highlighted below"* with nothing
 * highlighted, because the named key routes to an input that is not there.
 */
const WRITE_KEYS = Object.keys(emptyDraft());

/** How many invoices the picker asks for. 100 is the server's own ceiling. */
const INVOICE_PER_PAGE = 100;

export function CreditNoteAllocationsPanel({
  creditNote,
  onChanged,
  reloadSignal = 0,
}: CreditNoteAllocationsPanelProps) {
  const headingId = useId();
  const { ability } = useAuth();
  /*
   * The retry is a write gated server-side on `credit-notes.update`, so it is
   * offered on the same grant. The hidden control is the optimisation; a 403
   * still renders as the push outcome line.
   */
  const canSendToXero = ability.canUpdate('credit-notes');

  const [allocations, setAllocations] = useState<CreditNoteAllocation[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [draft, setDraft] = useState(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formMessages, setFormMessages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<CreditNoteAllocation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * What the last apply or retry did in Xero, shown beside the panel. One line,
   * replaced by the next write — the row's own Xero cell is the durable record.
   */
  const [pushOutcome, setPushOutcome] = useState<XeroOutcomeLine | null>(null);
  const [pushingId, setPushingId] = useState<number | null>(null);

  const invoices = useInvoiceOptions(creditNote);

  /*
   * ⚠️ The previous rows are kept on screen while a refresh is in flight rather
   * than blanking back to skeletons. A write here always triggers a refetch, and
   * a table that empties and refills on every save reads as the rows having been
   * destroyed and rebuilt.
   */
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listCreditNoteAllocations(creditNote.id, signal)
      .then((result) => {
        if (signal.aborted) return;
        setAllocations(result.data);
        setLoadFailed(false);
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no rows, and telling
         * somebody this credit has been applied to nothing when the request did
         * not come back is a claim about money they will act on.
         */
        if (signal.aborted) return;
        setLoadFailed(true);
      });

    return () => controller.abort();
  }, [creditNote.id, reloadToken, reloadSignal]);

  /*
   * 🔴 BOTH HALVES, OR THE FORM IS WITHHELD — and the order matters. A voided
   * note can carry a `remainingAmount` above zero, so the spendable test has to
   * answer first or the reader is told the note is fully applied when it is
   * withdrawn.
   */
  const spendable = spendableOf(creditNote);
  const hasCreditLeft = isPositiveDecimal(creditNote.remainingAmount);
  const applyDenial = !spendable
    ? NOT_SPENDABLE_REASON
    : !hasCreditLeft
      ? NOTHING_LEFT_REASON
      : null;

  /*
   * ⚠️ **`CREDIT_AVAILABLE_LABEL` may only sit over a figure that passed
   * `spendableOf`** — it is a claim about what the figure can DO, and over an
   * unapproved note it states that money is spendable when the server says it
   * is not. `CREDIT_REMAINING_LABEL` is the neutral fact, safe in every state.
   */
  const figureLabel = spendable ? CREDIT_AVAILABLE_LABEL : CREDIT_REMAINING_LABEL;

  function resetErrors() {
    setFieldErrors({});
    setFormMessages([]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (applyDenial !== null || saving) return;

    const refusals: Record<string, string> = {};
    if (draft.invoiceId === '') refusals.invoiceId = INVOICE_REQUIRED;
    const amountProblem = amountRefusal(draft.amount);
    if (amountProblem !== null) refusals.amount = amountProblem;
    if (Object.keys(refusals).length > 0) {
      setFieldErrors(refusals);
      setFormMessages([]);
      return;
    }

    setSaving(true);
    resetErrors();
    setPushOutcome(null);
    try {
      const response = await api.createCreditNoteAllocation(creditNote.id, {
        /*
         * ⚠️ **`Number()` ON AN ID IS NOT THE RULE THIS FILE FORBIDS.** The
         * prohibition is about MONEY — an exact decimal a float cannot hold.
         * `invoiceId` is an integer key the picker carries as a string because
         * a `<select>` value is one, and the contract asks for a number.
         */
        invoiceId: Number(draft.invoiceId),
        /*
         * ⚠️ **The typed string, trimmed — never a `Number()` round trip.** It
         * has already been through the exact parser, and reformatting money on
         * the way out is how a figure a person typed stops matching the figure
         * they are billed.
         */
        amount: draft.amount.trim(),
      });
      setDraft(emptyDraft());
      /*
       * ⚠️ The allocation SAVED whatever the push says — a `failed` push keeps
       * it, and the balance already counts it. `?.` because an older server
       * answers `{data}` alone, and then there is nothing to report.
       */
      if (response.xeroPush) setPushOutcome(allocationPushOutcome(response.xeroPush, 'apply'));
      setReloadToken((token) => token + 1);
      onChanged();
    } catch (error) {
      reportWriteFailure(error, setFieldErrors, setFormMessages);
    } finally {
      setSaving(false);
    }
  }

  /**
   * Retry the push of one allocation that is not in Xero. A refusal of the
   * REQUEST (a 403, a vanished row) is worded as a refusal line; a refusal by
   * XERO arrives as `xeroPush: failed` on a 200.
   */
  async function sendToXero(allocation: CreditNoteAllocation) {
    if (pushingId !== null) return;
    setPushingId(allocation.id);
    setPushOutcome(null);
    try {
      const response = await api.pushCreditNoteAllocation(creditNote.id, allocation.id);
      setPushOutcome(allocationPushOutcome(response.xeroPush, 'retry'));
      setReloadToken((token) => token + 1);
      onChanged();
    } catch (error) {
      setPushOutcome({
        tone: 'danger',
        text:
          error instanceof ApiError
            ? (error.formMessage ?? error.message)
            : 'That could not be sent to Xero.',
      });
    } finally {
      setPushingId(null);
    }
  }

  async function confirmDelete() {
    if (pendingDelete === null || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCreditNoteAllocation(creditNote.id, pendingDelete.id);
      setPendingDelete(null);
      setReloadToken((token) => token + 1);
      onChanged();
    } catch (error) {
      /*
       * ⚠️ The server's own sentence, verbatim. A refusal here is about this
       * allocation — the row may have gone already, or the note may have moved
       * — and a house sentence in front of it would replace the only actionable
       * half.
       *
       * `XERO_ALLOCATION_REMOVE_FAILED` (2026-10-03): Xero would not remove an
       * allocation it holds, so NOTHING was removed here either. The server's
       * sentence leads and Xero's own reason, where the server passed one on,
       * follows — never one in place of the other.
       */
      if (error instanceof ApiError && error.code === XERO_ALLOCATION_REMOVE_FAILED_CODE) {
        const reasons = error.detailReasons.filter((reason) => reason !== error.message);
        setDeleteError([error.message, ...reasons].join(' '));
      } else {
        setDeleteError(
          error instanceof ApiError ? (error.formMessage ?? error.message) : 'That did not save.',
        );
      }
    } finally {
      setDeleting(false);
    }
  }

  const rows = allocations ?? [];

  return (
    // `min-w-0`: this sits in the page's grid, and without it the allocations
    // table's min-content widened the whole track to ~760px on a phone.
    <Panel className="min-w-0" aria-labelledby={headingId}>
      <PanelHeader
        id={headingId}
        title={PANEL_TITLE}
        actions={
          <p className="text-xs text-fg-muted">
            {figureLabel}{' '}
            <Money
              amount={creditNote.remainingAmount}
              currency={creditNote.currency}
              className="font-medium text-fg"
            />
          </p>
        }
      />

      <PanelBody className="border-b border-border">
        <form className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_12rem]" onSubmit={submit}>
          <Field label="Invoice" error={fieldErrors.invoiceId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={draft.invoiceId}
                onChange={(value) => setDraft((current) => ({ ...current, invoiceId: value }))}
                options={invoices.options}
                onSearch={invoices.setTerm}
                loading={invoices.loading}
                {...(invoices.footnote ? { footnote: invoices.footnote } : {})}
                placeholder="Choose an invoice"
                searchPlaceholder="Search invoices"
                /*
                 * ⚠️ Disabled on a FAILED read, not left as an empty list: an
                 * empty picker is indistinguishable from a company with no
                 * invoices, and the sentence beneath says which this is.
                 */
                disabled={applyDenial !== null || invoices.failed}
                invalid={fieldErrors.invoiceId !== undefined}
              />
            )}
          </Field>

          {/*
            ⚠️ The currency is in the LABEL rather than in a sentence under the
            box: the code is a fact about what is being typed, and a label is
            not helper text.
          */}
          <Field label={`Amount (${creditNote.currency})`} error={fieldErrors.amount}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={draft.amount}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, amount: event.target.value }))
                }
                /*
                 * `inputMode` rather than `type="number"`: a number input hands
                 * back a browser-normalised value and a spinner that steps money,
                 * and this field carries an exact decimal string.
                 */
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                className="text-right tabular-nums"
                disabled={applyDenial !== null}
                invalid={fieldErrors.amount !== undefined}
              />
            )}
          </Field>

          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            <Button type="submit" loading={saving} disabled={applyDenial !== null}>
              {APPLY_LABEL}
            </Button>
          </div>

          {/* EXCEPTION 2 — why the controls above are disabled. */}
          {applyDenial !== null && (
            <p className="text-xs text-fg-muted sm:col-span-2">{applyDenial}</p>
          )}

          {/*
            EXCEPTION 4 — the picker is empty or unread, and those are different
            facts from each other and from "this company has no invoices".
            Suppressed while a search term is in play: an empty result for a
            typed term says nothing about the company.
          */}
          {applyDenial === null && invoices.failed && (
            <p className="text-xs text-fg-muted sm:col-span-2">{INVOICES_UNREADABLE}</p>
          )}
          {applyDenial === null &&
            !invoices.failed &&
            !invoices.loading &&
            invoices.term === '' &&
            invoices.options.length === 0 && (
              <p className="text-xs text-fg-muted sm:col-span-2">
                {NO_INVOICES_TO_APPLY_TO(creditNote.currency)}
              </p>
            )}

          {/*
            EXCEPTION 1 — the server's refusals that name no control this form
            renders. ⚠️ Never counted toward a "highlighted below" summary.
          */}
          {formMessages.length > 0 && (
            <Notice tone="danger" role="alert" className="sm:col-span-2">
              {formMessages.map((message) => (
                <p key={message}>{message}</p>
              ))}
            </Notice>
          )}
        </form>

        {/*
          EXCEPTION 4 — what the last apply or retry did in Xero. ⛔ `neutral`
          (a skip) is never toned as an error: it is the ordinary answer while
          the note or invoice is not in Xero.
        */}
        {pushOutcome !== null && <XeroOutcome line={pushOutcome} className="mt-3" />}
      </PanelBody>

      {loadFailed && (
        <div className="border-b border-border px-4 py-2">
          <Notice tone="danger" role="alert">
            {LIST_UNREADABLE}
          </Notice>
        </div>
      )}

      {/*
        🔴 **THREE STATES, AND THE THIRD IS THE ONE THAT MATTERS.** A read that
        FAILED renders the banner above and NOTHING here: falling through to the
        empty state would tell a reader that none of this credit has been
        applied, which is a claim about money made from a request that never
        came back. Unread and empty are different facts.
      */}
      {allocations === null ? (
        loadFailed ? null : (
          <LoadingRows />
        )
      ) : rows.length === 0 ? (
        <EmptyState
          title={NOTHING_APPLIED_TITLE}
          description={
            applyDenial === null
              ? `Use ${APPLY_LABEL} above to put some of it against an invoice.`
              : applyDenial
          }
        />
      ) : (
        <PanelBody density="flush">
          <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
            {/* 760, not 680, since 2026-10-03: the Xero cell can carry a button. */}
            <Table className="min-w-[760px]">
              <THead>
                <TR>
                  <TH>Invoice</TH>
                  <TH numeric>Amount</TH>
                  <TH>Applied on</TH>
                  <TH>Applied by</TH>
                  <TH>Xero</TH>
                  <TH pinned>
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((allocation) => (
                  <AllocationRow
                    key={allocation.id}
                    allocation={allocation}
                    currency={creditNote.currency}
                    onUnapply={() => {
                      setDeleteError(null);
                      setPendingDelete(allocation);
                    }}
                    onSendToXero={canSendToXero ? () => void sendToXero(allocation) : null}
                    sending={pushingId === allocation.id}
                    sendDisabled={pushingId !== null && pushingId !== allocation.id}
                  />
                ))}
              </TBody>
            </Table>
          </TableWrap>
        </PanelBody>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title={UNAPPLY_TITLE}
        confirmLabel={UNAPPLY_LABEL}
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
        description={
          pendingDelete === null ? null : (
            <UnapplyConsequence allocation={pendingDelete} currency={creditNote.currency} />
          )
        }
      />
    </Panel>
  );
}

/**
 * 🔴 **EXCEPTION 3 — the destructive consequence, and it names BOTH records
 * that move.** Removing an allocation gives the credit back to the note **and
 * the invoice owes that money again**; a dialog naming only the first describes
 * half of what the button does, and the half it leaves out is the one a finance
 * reader needs.
 *
 * ⛔ **NOTHING HERE PROMISES AN UNDO, IN ANY FORM.** Un-applying DELETES the
 * allocation row. There is no contra entry and no record that the allocation
 * existed, so *"you can re-apply it"* is a promise about a row that will not be
 * there — and the figures would not be the same allocation restored, they would
 * be a new one.
 *
 * ⚠️ **The last sentence is where rule 5 is discharged.** An allocation is
 * changed by un-applying it and applying the credit again, and this is the
 * screen a reader reaches when they went looking for an Edit — so it names the
 * two controls that DO exist rather than leaving the one they expected reading
 * as an oversight. ⛔ **It names them rather than asserting that no edit
 * exists**: a claim about what the API lacks rots the moment somebody builds
 * it, and the builder is never the one re-reading this sentence. **The
 * observable event that would move it: a PATCH appearing on an allocation.**
 */
function UnapplyConsequence({
  allocation,
  currency,
}: {
  allocation: CreditNoteAllocation;
  currency: string;
}) {
  const figure = money(allocation.amount, currency);
  const name = invoiceName(allocation);
  /*
   * ⚠️ A `null` invoice is an allocation whose target this read could not
   * resolve — so the sentence says so rather than naming an invoice it cannot
   * name, and the consequence is stated in full either way.
   */
  const target = name ?? 'the invoice it was applied to';

  return (
    <div className="space-y-2">
      <p>
        This removes the {figure} applied to {target}.
      </p>
      <p>
        The credit goes back to this credit note, and {target} owes that {figure} again.
      </p>
      <p>
        The allocation is deleted: nothing afterwards records that it existed, and applying this
        credit again creates a new allocation rather than restoring this one.
      </p>
      <p>
        To change an amount, {UNAPPLY_LABEL.toLowerCase()} it here and apply the credit again.
      </p>
      {/* 2026-10-03 — an allocation Xero holds is removed there first. */}
      {allocationInXero(allocation) && <p>{UNAPPLY_REMOVES_FROM_XERO}</p>}
    </div>
  );
}

/**
 * One push outcome line. Shared with the detail page, which reports the
 * approve and void pushes the same way, so the two cannot tone one fact
 * differently.
 *
 * ⛔ Only `danger` is an alert; `neutral` is a skip and reads as one.
 */
export function XeroOutcome({ line, className }: { line: XeroOutcomeLine; className?: string }) {
  return (
    <p
      role={line.tone === 'danger' ? 'alert' : 'status'}
      className={cx(
        'm-0 min-w-0 text-xs [overflow-wrap:anywhere]',
        line.tone === 'success' && 'text-success',
        line.tone === 'neutral' && 'text-fg-muted',
        line.tone === 'danger' && 'text-danger',
        className,
      )}
    >
      {line.text}
    </p>
  );
}

function AllocationRow({
  allocation,
  currency,
  onUnapply,
  onSendToXero,
  sending,
  sendDisabled,
}: {
  allocation: CreditNoteAllocation;
  currency: string;
  onUnapply: () => void;
  /** `null` when this reader may not send — the control is withheld then. */
  onSendToXero: (() => void) | null;
  sending: boolean;
  sendDisabled: boolean;
}) {
  const name = invoiceName(allocation);

  return (
    <TR>
      <TD>
        {allocation.invoice === null ? (
          /*
           * ⚠️ Not the same dash as an empty cell: this one says the row's
           * target could not be resolved on this read, which is a fact about
           * the data rather than about the invoice.
           */
          <span className="text-fg-muted" title="This allocation's invoice could not be resolved.">
            —
          </span>
        ) : (
          <TextLink to={`/invoices/${allocation.invoice.id}`}>{name}</TextLink>
        )}
      </TD>
      <TD numeric className="tabular-nums whitespace-nowrap">
        <Money amount={allocation.amount} currency={currency} />
      </TD>
      <TD className="whitespace-nowrap">{formatDateTime(allocation.allocatedAt)}</TD>
      <TD>
        {allocation.allocatedBy === null
          ? '—'
          : allocation.allocatedBy.name.trim() || allocation.allocatedBy.email}
      </TD>
      <TD>
        <XeroCell
          allocation={allocation}
          onSendToXero={onSendToXero}
          sending={sending}
          sendDisabled={sendDisabled}
        />
      </TD>
      <TD pinned className="text-right">
        <Button variant="ghost" size="sm" onClick={onUnapply}>
          {UNAPPLY_LABEL}
        </Button>
      </TD>
    </TR>
  );
}

/**
 * Whether Xero holds this allocation, and — when it does not — the retry.
 *
 * ⚠️ *SUPERSEDED 2026-10-03: "today nothing stamps this column at all, because
 * the push is a later batch … ⛔ **No danger or warning tone, no "sync
 * failed", and no retry control** … a retry button whose only outcome is
 * nothing".* The push now runs after every apply (owner's decision), so a
 * retry has a real outcome and is offered. **The half that stands: "Not in
 * Xero" is still not a failure** — it is the ordinary state while the note or
 * the invoice is not in Xero, so it carries no danger or warning tone. Why a
 * particular push did not land is the outcome line above the table.
 *
 * ⚠️ **"In Xero" is not proof the two systems agree today** — it records what a
 * push WROTE, which is the proxy-field shape. The date it was written is on the
 * badge's title and it is worded as nothing more.
 */
function XeroCell({
  allocation,
  onSendToXero,
  sending,
  sendDisabled,
}: {
  allocation: CreditNoteAllocation;
  onSendToXero: (() => void) | null;
  sending: boolean;
  sendDisabled: boolean;
}) {
  if (allocationInXero(allocation)) {
    return (
      <Badge
        tone="neutral"
        {...(allocation.xeroAllocatedAt !== null
          ? { title: formatDateTime(allocation.xeroAllocatedAt) }
          : {})}
      >
        {ALLOCATION_IN_XERO_LABEL}
      </Badge>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="whitespace-nowrap text-fg-muted">{ALLOCATION_NOT_IN_XERO_LABEL}</span>
      {onSendToXero !== null && (
        <Button
          variant="secondary"
          size="sm"
          loading={sending}
          disabled={sendDisabled}
          onClick={onSendToXero}
        >
          {SEND_ALLOCATION_TO_XERO_LABEL}
        </Button>
      )}
    </div>
  );
}

/** Skeletons in the real shape of the table, not a spinner. */
function LoadingRows() {
  return (
    <PanelBody density="flush">
      <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
        <Table className="min-w-[760px]">
          <THead>
            <TR>
              <TH>Invoice</TH>
              <TH numeric>Amount</TH>
              <TH>Applied on</TH>
              <TH>Applied by</TH>
              <TH>Xero</TH>
              <TH pinned>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {[0, 1].map((row) => (
              <TR key={row}>
                <TD>
                  <Skeleton className="h-4 w-40" />
                </TD>
                <TD numeric>
                  <Skeleton className="ml-auto h-4 w-20" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-28" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-24" />
                </TD>
                <TD>
                  <Skeleton className="h-4 w-20" />
                </TD>
                <TD pinned>
                  <Skeleton className="ml-auto h-4 w-16" />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    </PanelBody>
  );
}

/**
 * The invoices this credit can be applied to, searched server-side.
 *
 * ⚠️ **A FILTER OFFERS WHAT CAN MATCH.** The company and the currency are both
 * the server's own refusals — an invoice on another company or in another
 * currency is a 422 naming `invoiceId` — so narrowing on them offers only what
 * can be chosen rather than options whose one outcome is a refusal.
 *
 * ⚠️ **`unsettled` is WIDER than "outstanding" and drafts are in it**, which is
 * why no control here is labelled with either word. A settled invoice owes
 * nothing and can take no credit, so leaving them out removes options that
 * could only ever fail; a draft is the server's to accept or refuse.
 *
 * ⚠️ **A failed read is `failed`, never an empty list.** Both render no
 * options, and telling somebody this company has no invoices when the request
 * did not come back sends them to raise one that already exists — the same
 * defect this repo already shipped on a staff picker.
 *
 * ⚠️ **`perPage` is the server's own ceiling of 100, so the list can be short**
 * — the footnote says so, and `onSearch` is what makes an option past the cut
 * reachable at all. A bigger number is not available; there is no larger value
 * to reach for.
 */
function useInvoiceOptions(creditNote: CreditNote) {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [total, setTotal] = useState(0);
  const [failed, setFailed] = useState(false);
  /*
   * The term the loaded options answer — null until the first read settles.
   * Deriving `loading` from it rather than holding a flag means a new term reads
   * as loading during the very render that set it, with no setState inside the
   * effect to start a second render pass.
   */
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = loadedTerm !== term;

  const companyId = creditNote.company.id;
  const currency = creditNote.currency;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listInvoices(
        {
          page: 1,
          perPage: INVOICE_PER_PAGE,
          companyId,
          currency,
          unsettled: true,
          ...(term ? { search: term } : {}),
        },
        signal,
      )
      .then((result) => {
        if (signal.aborted) return;
        setOptions(
          result.data.map((invoice) => ({
            value: String(invoice.id),
            label: documentDisplayTitle(invoice.subject, invoice.xeroInvoiceNumber),
            /*
             * ⚠️ The figure carries its own code, and the invoice's own — not
             * the note's. A currency-less import is rendered without one rather
             * than being handed a code it does not claim.
             */
            hint:
              invoice.currency === null
                ? formatDecimalString(invoice.grandTotal)
                : money(invoice.grandTotal, invoice.currency),
          })),
        );
        setTotal(result.meta.total);
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [companyId, currency, term]);

  const footnote =
    total > options.length ? PICKER_TRUNCATED(options.length, total) : undefined;

  return { options, loading, failed, term, setTerm, footnote };
}

/**
 * Routes a write failure to the controls that can carry it.
 *
 * 🔴 **THE SERVER'S FOUR REFUSALS ARE RENDERED VERBATIM AND NOTHING IS PUT IN
 * FRONT OF THEM.** A different company, a different currency, more than the
 * note has left, and more than the invoice still owes each arrive as a `422`
 * naming `invoiceId` or `amount` — and they send a person to four different
 * places, which the server's own sentences already say. A house sentence in
 * front of them would replace the only actionable half with a summary.
 *
 * ⚠️ **`routeFieldErrors` rather than `fieldErrors`**, so a path naming a key
 * this form does not render lands in the banner instead of marking an
 * off-screen input — which is the failure that shows *"one field needs
 * attention, highlighted below"* with nothing highlighted.
 */
function reportWriteFailure(
  error: unknown,
  setFieldErrors: (value: Record<string, string>) => void,
  setFormMessages: (value: string[]) => void,
) {
  if (!(error instanceof ApiError)) {
    setFieldErrors({});
    setFormMessages(['That did not save.']);
    return;
  }

  const { fields, unroutable } = error.routeFieldErrors(WRITE_KEYS);
  setFieldErrors(fields);

  if (unroutable.length > 0) {
    setFormMessages(unroutable);
    return;
  }
  /*
   * ⚠️ Ordered by SPECIFICITY, never by the shape met first: a refusal that
   * named a control is already on that control, and only a refusal that named
   * none needs the banner. `message` on a 422 is the generic "Validation
   * failed", which is why it is last.
   */
  if (Object.keys(fields).length === 0) {
    setFormMessages([error.formMessage ?? error.message]);
    return;
  }
  setFormMessages([]);
}
